"use client";
import {
  createNewChat,
  getAISuggestedQuestions,
  getQuerySuggestions,
  getSampleData,
  getSuggestedTemplateQuestion,
} from "@/lib/api-client";

import useChat from "./useChat";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import { useEffect } from "react";

interface ChatSuggestionProps {
  q_id: string;
  question: string;
}

const useServerAction = () => {
  const router = useRouter();

  const {
    isChatStarted,
    chatStartedFn,
    chatEndFn,
    hasTemplateQuestion,
    hasSuggestedQuestion,
    toggleTemplateQuestion,
    saveTemplateQuestion,
    handleTemplateQuestionLoading,
    toggleSuggestedQuestion,
    handleSuggestedQuestionLoading,
    saveSuggestedQuestion,
    saveChatSuggestion,
    handleChatWindowLoading,
  } = useChat();

  const queryClient = useQueryClient();

  // Get Suggested Question
  const fetchTemplateQuestion = async () => {
    if (hasTemplateQuestion) {
      toggleTemplateQuestion(true);
      return;
    }

    handleTemplateQuestionLoading(true);
    try {
      const response = await getSuggestedTemplateQuestion();

      const hasQuestions =
        Array.isArray(response?.questions) && response.questions.length > 0;

      if (!hasQuestions) {
        toggleTemplateQuestion(false);
        return;
      }
      saveTemplateQuestion(response.questions);
      toggleTemplateQuestion(true);
    } catch (error) {
    } finally {
      handleTemplateQuestionLoading(false);
    }
  };

  const { mutate: getSampleDataFn } = useMutation({
    mutationFn: (data: any) => getSampleData(data),
    onMutate: () => chatStartedFn(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["chats"] });
    },
    onSettled: () => chatEndFn(),
  });

  // Chat Suggestion
  const fetchChatSuggestion = async () => {
    if (hasSuggestedQuestion) {
      toggleSuggestedQuestion(true);
      handleSuggestedQuestionLoading(true);
      return;
    }

    handleSuggestedQuestionLoading(true);
    try {
      const response = await getAISuggestedQuestions();

      const hasQuestions =
        Array.isArray(response?.questions) && response?.questions?.length > 0;

      if (!hasQuestions) {
        toggleSuggestedQuestion(false);
        return;
      }

      saveSuggestedQuestion(response.questions);
      toggleSuggestedQuestion(true);
    } catch (error) {
    } finally {
      handleSuggestedQuestionLoading(false);
    }
  };

  // Query Bank Suggestion
  const getQueryBankSuggestionFn = async () => {
    try {
      const response = await getQuerySuggestions();

      const hasQuestions =
        Array.isArray(response?.result) && response.result.length > 0;

      if (!hasQuestions) {
        return;
      }

      const extractedQuestions = (response.result ?? []).map(
        (item: ChatSuggestionProps) => ({
          q_id: item.q_id,
          question: item.question,
        })
      );

      saveChatSuggestion(extractedQuestions);
    } catch (error) {}
  };

  // Handle Create New Chat
  const {
    data,
    mutate: handleCreateFn,
    isPending: isCreating,
    isSuccess: isCreatedChat,
  } = useMutation({
    mutationFn: createNewChat,
    onMutate: () => handleChatWindowLoading(true),
    onSuccess: async (data) => {
      //Destructuring chat id and redirecting to chat id
      const { chatId: newChatId } = data;
      if (newChatId) {
        // await queryClient.invalidateQueries({ queryKey: ["chat-history"] });
        // queryClient.refetchQueries({ queryKey: ["chat-history"] });
        if (queryClient.isFetching()) {
        }
        router.push(`/chatbot/${newChatId}`, { scroll: false });
      }
    },
    onError: (error) => {
      toast.error("Failed to create chat", { position: "bottom-right" });
    },
  });

  return {
    fetchTemplateQuestion,
    getSampleDataFn,
    fetchChatSuggestion,
    getQueryBankSuggestionFn,
    handleCreateFn,
  };
};

export default useServerAction;
